import type { BuilderChannel } from "campaign-builder/config/channelConfig";
import { CountryType, CitySettingsType } from "store/types";
import { CITY_STATUS_ACTIVE } from "shared/constants";
import { convertArrayToHashMap } from "shared/libraries/dataSets";
import { directMailOnlyCities } from "constants";
import { platformUSACities } from "pages/constants";
import { getCities } from "graphQL/query/cities";

// A city eligible for the campaign's channel, in the lean shape the Targeting
// flow needs (selection, map centring, and persistence into selectedCities).
export interface TargetingCity {
  id: number;
  name: string;
  nameWithState: string;
  lat: number;
  lng: number;
  postalCode: string;
  salesTaxRate: number;
}

// Per-city enablement flag that gates which cities a channel can target.
const CHANNEL_CITY_FILTER: Record<Extract<BuilderChannel, "H2H" | "D2D">, keyof CitySettingsType> = {
  H2H: "enabledChannelPreciseLocations",
  D2D: "enabledChannelLetterbox",
};

const byName = (a: TargetingCity, b: TargetingCity) => a.name.localeCompare(b.name);

// Loads the active cities for the account country, keeping only those whose
// per-city settings enable the given channel. Mirrors the legacy
// getFilteredCities filter so H2H/D2D see exactly the same city set as today.
export const getTargetingCities = async (
  channel: Extract<BuilderChannel, "H2H" | "D2D">,
  country: CountryType
): Promise<TargetingCity[]> => {
  const filterKey = CHANNEL_CITY_FILTER[channel];
  const cities = await getCities({
    status: CITY_STATUS_ACTIVE,
    countryId: country.id,
    includeSalesTax: true,
  });

  const settingsById = convertArrayToHashMap(country.citiesSettings, "id");

  return (cities ?? [])
    .reduce<TargetingCity[]>((acc, city: any) => {
      if (directMailOnlyCities.includes(city.name)) return acc;
      const settings = settingsById[city.id];
      if (!settings || !settings[filterKey]) return acc;
      acc.push({
        id: Number(city.id),
        name: city.name,
        nameWithState: platformUSACities[city.name] ? `${city.name}, ${platformUSACities[city.name]}` : city.name,
        lat: city.location.latitude,
        lng: city.location.longitude,
        postalCode: city.postalCode,
        salesTaxRate: city.salesTaxRate,
      });
      return acc;
    }, [])
    .sort(byName);
};
